import React, { FC } from 'react';
import TableSimple from 'src/components/TableSimple';
import { CustomRenderType, TableColumnType, TableRowType } from 'src/types/table';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import translate from '@translate';
import TextFieldDebounced from 'src/components/TextFieldDebounced';
import { EditConstantsCodeType } from '.';
import { sizes } from 'src/themes/default';
import { ConstantsCodeType } from 'src/types/constantsCodes';
import Select from 'src/components/Select';
import { langCodes } from 'src/resources/constantsCodes/constants';
import { FormErrorsType } from '../../types/global';

export type EditKeysType =
    | 'rawLabel'
    | 'value'
    | 'shipperCode'
    | 'isReturn'
    | 'isExpedition'
    | 'LANG'
    | 'COUNTRY_SERVICE_CODES';

type ItemsTablePropsType = {
    data?: EditConstantsCodeType[];
    onChange: (value: any, error: boolean, errorKey?: string) => void;
    disableEdit?: boolean;
    editErrors: FormErrorsType;
    allColumns: TableColumnType[];
    constantKey: string;
    allConstantsCodes: {
        [key: string]: ConstantsCodeType[];
    };
    countryCodes: ConstantsCodeType[];
};

const ItemsTable: FC<ItemsTablePropsType> = (props) => {
    const handleRemoveRow = (row: TableRowType) => {
        props.onChange(
            props.data?.filter((item) => item.ID != row.ID),
            false
        );
    };

    const getInputName = (id: number, key: string) => `item-${id}-${key}`;

    const changeValue = (key: EditKeysType, value: any, error: boolean, id: number) => {
        const newData = props.data?.map((item) => {
            if (item.ID === id) {
                const updatedItem = { ...item };
                updatedItem[key] = value;
                return updatedItem;
            }
            return item;
        });
        props.onChange(newData, error, getInputName(id, key));
    };

    const customRender: CustomRenderType = (column, row) => {
        const name = getInputName(row.ID, column.key);
        switch (column.key) {
            case 'rawLabel':
                return (
                    <TextFieldDebounced
                        value={row.rawLabel}
                        error={props.editErrors[name]}
                        onChange={(value, error) => changeValue('rawLabel', value, error, row.ID)}
                        name={name}
                        fullWidth
                        required
                        disabled={props.disableEdit}
                        multiline={props.constantKey === 'shipperMessageCodes'}
                    />
                );
            case 'value':
                return (
                    <TextFieldDebounced
                        value={row.value}
                        error={props.editErrors[name]}
                        onChange={(value, error) => changeValue('value', value, error, row.ID)}
                        name={name}
                        fullWidth
                        required
                        disabled={props.disableEdit}
                        multiline={props.constantKey === 'shipperMessageCodes'}
                    />
                );
            case 'color':
                return <div style={{ backgroundColor: row.color }}>{row.color}</div>;
            case 'shipperCode':
                return (
                    <Select
                        options={[...props.allConstantsCodes.shipperCodes].sort((a, b) =>
                            a.label.localeCompare(b.label)
                        )}
                        value={row.shipperCode}
                        error={props.editErrors[name]}
                        onChange={(value, error) => changeValue('shipperCode', value, error, row.ID)}
                        name={name}
                        fullWidth
                        required
                        disabled={props.disableEdit}
                    />
                );
            case 'isReturn':
                return (
                    <Select
                        options={props.allConstantsCodes.boolNumberCodes}
                        value={row.isReturn}
                        error={props.editErrors[name]}
                        onChange={(value, error) => changeValue('isReturn', value, error, row.ID)}
                        name={name}
                        fullWidth
                        required
                        disabled={props.disableEdit}
                    />
                );
            case 'isExpedition':
                return (
                    <Select
                        options={props.allConstantsCodes.boolNumberCodes}
                        value={row.isExpedition}
                        error={props.editErrors[name]}
                        onChange={(value, error) => changeValue('isExpedition', value, error, row.ID)}
                        name={name}
                        fullWidth
                        required
                        disabled={props.disableEdit}
                    />
                );
            case 'LANG':
                return (
                    <Select
                        options={langCodes}
                        value={row.LANG}
                        error={props.editErrors[name]}
                        onChange={(value, error) => changeValue('LANG', value, error, row.ID)}
                        name={name}
                        fullWidth
                        required
                        disabled={props.disableEdit}
                    />
                );
            case 'countryCodesSelect':
                return (
                    <Select
                        options={props.countryCodes}
                        value={row.COUNTRY_SERVICE_CODES}
                        error={props.editErrors[name]}
                        onChange={(value, error) => changeValue('COUNTRY_SERVICE_CODES', value, error, row.ID)}
                        name={name}
                        fullWidth
                        disabled={props.disableEdit}
                        multiple
                    />
                );
        }
        return null;
    };

    return (
        <TableSimple
            rowIdKey="ID"
            rows={props.data || []}
            columsLabelPrefix="constantDetail.table"
            allColumns={props.allColumns}
            initSort={{ column: 'ID', direction: 'DESC' }}
            rowActions={
                props.disableEdit
                    ? undefined
                    : [
                          {
                              icon: <HighlightOffIcon />,
                              label: translate('constantDetail.action.removeItem'),
                              onClick: handleRemoveRow,
                              style: { height: sizes.table_action_with_input_H },
                              hiddenFce: () => !!props.disableEdit,
                          },
                      ]
            }
            customRender={!props.disableEdit ? customRender : undefined}
            selectOptions={{
                shipperCodes: [...props.allConstantsCodes.shipperCodes].sort((a, b) =>
                    a.label.localeCompare(b.label)
                ),
                boolNumberCodes: props.allConstantsCodes.boolNumberCodes,
                boolCodes: props.allConstantsCodes.boolCodes,
                countryCodes: props.countryCodes,
                langCodes,
            }}
            maxHeight={'unset'}
        />
    );
};

export default ItemsTable;
